/**
 * GWP (Gift With Purchase) — Favourite Daughter (FD-1159)
 *
 * Reads eligibility state from the #gwp-data JSON block rendered server-side
 * by snippets/gwp-selector.liquid inside sections/gwp-modal-metaobjects.liquid.
 *
 * When the cart changes (cart:updated / cart:added events), it re-fetches the
 * gwp-modal-metaobjects section via Shopify's Sections API to get fresh Liquid-evaluated
 * eligibility, then manages the popup / auto-remove logic.
 */
(function () {

  var gwpDebounceTimer;
  var gwpCartAddedTimer;
  var gwpStatusHideTimer;
  var gwpStatusMsg    = '';
  var gwpStatusState  = '';
  var gwpStatusGiftName = '';
  var gwpRemovalInProgress = false;
  var gwpAddInProgress    = false;
  var gwpSwapLineKey  = null;
  var gwpCurrentGifts = null;
  var gwpSelfRequest  = false;

  /* ------------------------------------------------------------------ */
  /*  Debug helper                                                        */
  /* ------------------------------------------------------------------ */
  function gwpLog() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[GWP]');
    console.log.apply(console, args);
  }

  /* ------------------------------------------------------------------ */
  /*  Data access                                                         */
  /* ------------------------------------------------------------------ */

  function gwpGetData() {
    var el = document.querySelector('#gwp-data');
    if (!el) {
      gwpLog('ERROR — #gwp-data element not found in DOM');
      return null;
    }
    try {
      var data = JSON.parse(el.textContent);
      gwpLog('gwpGetData:', JSON.stringify(data));
      return data;
    } catch (e) {
      gwpLog('ERROR — failed to parse #gwp-data JSON:', e.message, '| raw:', el.textContent.substring(0, 200));
      return null;
    }
  }

  /**
   * Re-fetch the gwp-modal-metaobjects section via the Sections Rendering API so Liquid
   * re-evaluates eligibility against the current cart state.
   */
  function gwpRefreshData(callback) {
    gwpLog('gwpRefreshData — fetching /?sections=gwp-modal-metaobjects …');
    fetch('/?sections=gwp-modal-metaobjects')
      .then(function (r) {
        gwpLog('gwpRefreshData — response status:', r.status);
        return r.json();
      })
      .then(function (sections) {
        gwpLog('gwpRefreshData — section keys:', Object.keys(sections));
        var html = sections['gwp-modal-metaobjects'] || '';
        gwpLog('gwpRefreshData — section HTML length:', html.length);

        var parser = new DOMParser();
        var doc    = parser.parseFromString(html, 'text/html');

        // Swap #gwp-data
        var newDataEl = doc.querySelector('#gwp-data');
        if (newDataEl) {
          gwpLog('gwpRefreshData — new #gwp-data content:', newDataEl.textContent.trim().substring(0, 300));
          var existing = document.querySelector('#gwp-data');
          if (existing) {
            existing.textContent = newDataEl.textContent;
            gwpLog('gwpRefreshData — #gwp-data updated in DOM');
          } else {
            gwpLog('gwpRefreshData — WARNING: no #gwp-data in live DOM to replace');
          }
        } else {
          gwpLog('gwpRefreshData — WARNING: no #gwp-data found in refreshed section HTML');
        }

        // Swap .gwp-selected-state
        var newState      = doc.querySelector('.gwp-selected-state');
        var existingState = document.querySelector('.gwp-selected-state');
        if (newState && existingState) {
          existingState.outerHTML = newState.outerHTML;
        } else if (newState && !existingState) {
          var section = document.querySelector('#shopify-section-gwp-modal-metaobjects');
          if (section) section.insertAdjacentHTML('beforeend', newState.outerHTML);
        } else if (!newState && existingState) {
          existingState.remove();
        }

        if (callback) callback(true);
      })
      .catch(function (err) {
        gwpLog('ERROR — gwpRefreshData fetch failed:', err.message);
        if (callback) callback(false);
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Cart display refresh (sidebar items, count, subtotal)               */
  /* ------------------------------------------------------------------ */

  function gwpRefreshCartDisplay() {
    if (/\/cart\/?$/.test(window.location.pathname)) {
      // Cart page: swap in fresh rows/subtotal instead of reloading the whole page,
      // then update the mini cart + header count below. Falls back to a reload.
      gwpRefreshCartPage().then(function (ok) {
        if (!ok) {
          window.location.reload();
          return;
        }
        gwpRefreshMiniCart();
      });
      return;
    }
    gwpRefreshMiniCart();
  }

  /**
   * Re-render the cart page section (Section Rendering API) and replace only the
   * item rows and subtotal/savings. The footer's checkout + dynamic checkout buttons
   * (Shop Pay / PayPal) and the Klarna placement are left untouched so they don't
   * need re-initialising. If the cart is now empty, the whole section is swapped
   * for the empty-cart state. Resolves false if anything is missing.
   */
  function gwpRefreshCartPage() {
    var container = document.querySelector('.container[data-section-type="cart-template"]');
    var sectionId = container && container.getAttribute('data-section-id');
    if (!sectionId) return Promise.resolve(false);

    return fetch('/cart?section_id=' + encodeURIComponent(sectionId) + '&_cb=' + Date.now(), { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('section fetch failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newContainer = doc.querySelector('.container[data-section-type="cart-template"]');
        if (!newContainer) return false;

        // Cart emptied — no checkout buttons to preserve, swap the whole section
        if (!newContainer.querySelector('form.cart')) {
          container.innerHTML = newContainer.innerHTML;
          return true;
        }

        var oldBody = container.querySelector('form.cart tbody');
        var newBody = newContainer.querySelector('form.cart tbody');
        var oldSubtotal = container.querySelector('.cart__subtotal');
        var newSubtotal = newContainer.querySelector('.cart__subtotal');
        if (!oldBody || !newBody || !oldSubtotal || !newSubtotal) return false;

        gwpSyncCartRows(oldBody, newBody);
        oldSubtotal.innerHTML = newSubtotal.innerHTML;

        var oldSavings = container.querySelector('.cart__savings');
        var newSavings = newContainer.querySelector('.cart__savings');
        if (oldSavings && newSavings) {
          oldSavings.innerHTML = newSavings.innerHTML;
        } else if (oldSavings) {
          oldSavings.remove();
        } else if (newSavings) {
          oldSubtotal.parentNode.insertAdjacentElement('afterend', newSavings);
        }
        return true;
      })
      .catch(function (err) {
        gwpLog('ERROR — gwpRefreshCartPage failed:', err.message);
        return false;
      });
  }

  /**
   * Match cart rows by line key (data-key): drop rows no longer in the cart and
   * insert new ones in order. Rows present in both are left untouched, so the
   * handlers the cart page bound directly to them keep working. Only gift lines
   * change here (add / swap / remove) and those don't affect other lines' prices.
   */
  function gwpSyncCartRows(oldBody, newBody) {
    var oldByKey = {};
    Array.prototype.forEach.call(oldBody.querySelectorAll('tr[data-key]'), function (row) {
      oldByKey[row.getAttribute('data-key')] = row;
    });

    var kept = {};
    var prev = null;
    Array.prototype.forEach.call(newBody.querySelectorAll('tr[data-key]'), function (newRow) {
      var key = newRow.getAttribute('data-key');
      if (oldByKey[key]) {
        kept[key] = true;
        prev = oldByKey[key];
        // Line positions shift when a gift row is added/removed — keep data-line current
        var newLine = newRow.querySelector('[data-line]');
        var oldLine = prev.querySelector('[data-line]');
        if (newLine && oldLine) oldLine.setAttribute('data-line', newLine.getAttribute('data-line'));
        return;
      }
      var node = document.importNode(newRow, true);
      if (prev) {
        prev.insertAdjacentElement('afterend', node);
      } else {
        oldBody.insertBefore(node, oldBody.firstChild);
      }
      prev = node;
    });

    Object.keys(oldByKey).forEach(function (key) {
      if (!kept[key]) oldByKey[key].remove();
    });
  }

  /* Mini cart (sidebar) items, header count and sidebar subtotal */
  function gwpRefreshMiniCart() {
    if (typeof jQuery === 'undefined') {
      gwpLog('gwpRefreshCartDisplay — jQuery not available, skipping');
      return;
    }
    jQuery.ajax({
      type: 'GET',
      url: '/cart.js',
      dataType: 'json',
      headers: { 'Cache-Control': 'no-cache' },
      success: function (cartdata) {
        gwpLog('gwpRefreshCartDisplay — cart item_count:', cartdata.item_count, 'total_price:', cartdata.total_price);

        if (jQuery('#CartCount span').length === 0) {
          jQuery('#CartCount').append('<span>' + cartdata.item_count + '</span>');
        } else {
          jQuery('#CartCount span').eq(0).text(cartdata.item_count);
        }
        if (jQuery('.site-header__cart-count span').length === 0) {
          jQuery('.site-header__cart-count').append('<span>' + cartdata.item_count + '</span>');
        } else {
          jQuery('.site-header__cart-count span').text(cartdata.item_count);
        }

        jQuery('.cart-sidebar--items-wrpaper').load(
          location.href + ' .cart-sidebar--items-wrpaper > *',
          function () {
            if (jQuery('.cart-sidebar--items-wrpaper').text().trim() !== '') {
              jQuery('.cart-sidebar--footer').removeAttr('hidden');
            }
            // Reload footer subtotal from Liquid so it uses the correct presentment currency
            // (avoids hardcoded theme.moneyFormat which always uses the store's base currency symbol)
            jQuery('.cart-sidebar--footer-subtotal').load(
              location.href + ' .cart-sidebar--footer-subtotal > *'
            );
          }
        );

        // Notify other listeners but guard against infinite loop
        document.dispatchEvent(new CustomEvent('cart:updated'));
      },
      error: function (err) {
        gwpLog('gwpRefreshCartDisplay — cart.js fetch failed:', err.status);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Currency helpers                                                    */
  /* ------------------------------------------------------------------ */

  function gwpGetExchangeRate() {
    // Shopify Markets / most currency-switcher apps inject Shopify.currency.rate
    // which is the multiplier from the store's base currency to the active currency.
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
      var r = parseFloat(window.Shopify.currency.rate);
      if (r > 0) return r;
    }
    // Some older Shopify theme currency apps use window.Currency.rates keyed by currency code
    if (window.Currency && window.Currency.rates) {
      var active = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
      if (active && window.Currency.rates[active]) {
        var r2 = parseFloat(window.Currency.rates[active]);
        if (r2 > 0) return r2;
      }
    }
    return null;
  }

  function gwpCurrencyRecheck(data) {
    var isEligible = data.isEligible;
    if (!isEligible) return false;

    var t = data.threshold;
    if (!t || !t.cartCurrency) return isEligible;

    var rate = null;

    // Same-currency backstop — Liquid already enforces these, re-checked here so a
    // Liquid comparison issue can never let a below-threshold cart through
    if (t.minAmount > 0 && (!t.minCurrency || t.minCurrency === t.cartCurrency)
        && t.qualifyingTotalCents < Math.round(t.minAmount * 100)) {
      gwpLog('gwpCurrencyRecheck — below min (same currency): ' + t.qualifyingTotalCents + ' < ' + Math.round(t.minAmount * 100));
      return false;
    }
    if (t.maxAmount > 0 && (!t.maxCurrency || t.maxCurrency === t.cartCurrency)
        && t.qualifyingTotalCents > Math.round(t.maxAmount * 100)) {
      gwpLog('gwpCurrencyRecheck — above max (same currency)');
      return false;
    }

    // Min threshold cross-currency check
    if (t.minAmount > 0 && t.minCurrency && t.minCurrency !== t.cartCurrency) {
      rate = rate !== null ? rate : gwpGetExchangeRate();
      if (rate !== null) {
        var convertedMin = Math.round(t.minAmount * rate * 100);
        gwpLog('gwpCurrencyRecheck — min: qualifyingTotal=' + t.qualifyingTotalCents
          + ' convertedMin=' + convertedMin + ' rate=' + rate
          + ' (' + t.minCurrency + '→' + t.cartCurrency + ')');
        if (t.qualifyingTotalCents < convertedMin) {
          gwpLog('gwpCurrencyRecheck — below min threshold, overriding isEligible=false');
          return false;
        }
      } else {
        gwpLog('gwpCurrencyRecheck — no exchange rate available, skipping cross-currency min check');
      }
    }

    // Max threshold cross-currency check
    if (t.maxAmount > 0 && t.maxCurrency && t.maxCurrency !== t.cartCurrency) {
      rate = rate !== null ? rate : gwpGetExchangeRate();
      if (rate !== null) {
        var convertedMax = Math.round(t.maxAmount * rate * 100);
        gwpLog('gwpCurrencyRecheck — max: qualifyingTotal=' + t.qualifyingTotalCents
          + ' convertedMax=' + convertedMax + ' rate=' + rate);
        if (t.qualifyingTotalCents > convertedMax) {
          gwpLog('gwpCurrencyRecheck — above max threshold, overriding isEligible=false');
          return false;
        }
      }
    }

    return isEligible;
  }

  /* ------------------------------------------------------------------ */
  /*  UI — init (called after every data refresh)                         */
  /* ------------------------------------------------------------------ */

  function gwpInit(triggerSource) {
    gwpLog('gwpInit — triggered by:', triggerSource || 'unknown');
    var data = gwpGetData();
    if (!data) return;

    // Hide every "Change gift" link when the offer has only one possible gift
    document.documentElement.classList.toggle('gwp-no-change-gift', !data.canChangeGift);

    var isEligible = gwpCurrencyRecheck(data);

    // Lock checkout while an ineligible gift is still in the cart (released once it's removed)
    gwpSetCheckoutLock(data.gwpInCart && !isEligible);

    gwpLog('gwpInit — isEligible:', isEligible, '(raw:', data.isEligible, ') | gwpInCart:', data.gwpInCart);

    var selectedState = document.querySelector('.gwp-selected-state');

    if (isEligible && data.gwpInCart) {
      gwpLog('gwpInit — gift already in cart, showing selected-state bar');
      if (selectedState) selectedState.style.display = '';
      gwpHideStatus(0);

    } else if (isEligible && !data.gwpInCart) {
      gwpLog('gwpInit — eligible, no gift yet — opening popup');
      if (selectedState) selectedState.style.display = 'none';
      gwpOpenPopup(data);

    } else {
      gwpLog('gwpInit — not eligible');
      if (selectedState) selectedState.style.display = 'none';
      gwpClosePopup();
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Checkout lock                                                       */
  /* ------------------------------------------------------------------ */

  // Prevents checking out with a free gift the cart no longer qualifies for,
  // in the window between a cart change and the gift's auto-removal.
  function gwpSetCheckoutLock(locked) {
    document.documentElement.classList.toggle('gwp-checkout-locked', !!locked);
  }

  function gwpIsCheckoutLocked() {
    return document.documentElement.classList.contains('gwp-checkout-locked');
  }

  /* ------------------------------------------------------------------ */
  /*  Popup                                                               */
  /* ------------------------------------------------------------------ */

  function gwpOpenPopup(data) {
    gwpLog('gwpOpenPopup — offer:', data && data.offer ? data.offer.title : 'none');
    if (!data || !data.offer || !data.offer.gifts || !data.offer.gifts.length) {
      gwpLog('gwpOpenPopup — aborting: no gifts in offer');
      return;
    }

    if (!document.getElementById('gwp-popup')) {
      gwpBuildPopup(data);
    } else {
      gwpUpdatePopup(data);
      document.querySelectorAll('.gwp-select').forEach(function (b) {
        b.classList.remove('is-loading', 'is-pending', 'is-selected');
      });
      gwpMarkSelectedGifts(data);
      var existingPanel = document.getElementById('gwp-variant-panel');
      if (existingPanel) existingPanel.style.display = 'none';
    }

    var popup = document.getElementById('gwp-popup');
    if (popup) {
      popup.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      gwpLog('gwpOpenPopup — popup displayed');
    }
  }

  function gwpClosePopup() {
    var popup = document.getElementById('gwp-popup');
    if (popup && popup.style.display !== 'none') {
      popup.style.display = 'none';
      document.body.style.overflow = '';
      gwpLog('gwpClosePopup — popup hidden');
    }
  }

  function gwpBuildPopup(data) {
    gwpLog('gwpBuildPopup — building popup for', data.offer.gifts.length, 'gift(s)');
    var offer = data.offer;
    gwpCurrentGifts = offer.gifts;
    var giftsHtml = '';

    offer.gifts.forEach(function (gift, idx) {
      var imgTag = gift.imageUrl
        ? '<img class="gwp-select__image" src="' + gwpEscape(gift.imageUrl) + '" alt="' + gwpEscape(gift.productTitle) + '" loading="lazy">'
        : '';
      var hint = gift.hasVariants ? '<span class="gwp-select__variant-hint">Select size ›</span>' : '';

      giftsHtml +=
        '<button class="gwp-select"'
        + ' data-variant-id="' + gift.variantId + '"'
        + ' data-has-variants="' + gift.hasVariants + '"'
        + ' data-gift-idx="' + idx + '">'
        + '<span class="gwp-select__checkmark">✓</span>'
        + imgTag
        + '<span class="gwp-select__title">' + gwpEscape(gift.productTitle) + '</span>'
        + hint
        + '</button>';
    });

    var el = document.createElement('div');
    el.id  = 'gwp-popup';
    if (offer.gifts.length >= 4) el.classList.add('gwp-popup--wide');
    if (offer.gifts.length === 1) el.classList.add('gwp-popup--single');
    el.innerHTML =
      '<div id="gwp-popup__overlay"></div>'
      + '<div id="gwp-popup__card">'
      +   '<div id="gwp-popup__header">'
      +     '<span id="gwp-popup__title">' + gwpEscape(offer.title) + '</span>'
      +     '<button id="gwp-popup__close" aria-label="Close">×</button>'
      +   '</div>'
      +   '<p id="gwp-popup__subtitle">' + gwpEscape(offer.popupHeadline) + '</p>'
      +   '<div id="gwp-popup__grid" class="gwp-products-grid">' + giftsHtml + '</div>'
      +   '<div id="gwp-variant-panel" class="gwp-variant-panel" style="display:none">'
      +     '<p class="gwp-variant-panel__label">Select your size:</p>'
      +     '<div class="gwp-variant-panel__options"></div>'
      +   '</div>'
      +   '<div id="gwp-status-bar">'
      +     '<span class="gwp-status-spinner"></span>'
      +     '<span class="gwp-status-check">✓</span>'
      +     '<span class="gwp-status-text"></span>'
      +   '</div>'
      + '</div>';

    document.body.appendChild(el);
    gwpMarkSelectedGifts(data);
    gwpLog('gwpBuildPopup — popup appended to body');
  }

  function gwpMarkSelectedGifts(data) {
    if (!data || !data.gwpVariantIds || !data.gwpVariantIds.length) return;
    document.querySelectorAll('.gwp-select').forEach(function (b) {
      if (data.gwpVariantIds.indexOf(parseInt(b.dataset.variantId, 10)) !== -1) {
        b.classList.add('is-selected', 'is-loading');
      }
    });
  }

  function gwpUpdatePopup(data) {
    var titleEl = document.getElementById('gwp-popup__title');
    if (titleEl && data.offer) titleEl.textContent = data.offer.title;
    var subtitleEl = document.getElementById('gwp-popup__subtitle');
    if (subtitleEl && data.offer) subtitleEl.textContent = data.offer.popupHeadline;
  }

  function gwpEscape(str) {
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(String(str == null ? '' : str)));
    return d.innerHTML;
  }

  /* ------------------------------------------------------------------ */
  /*  Gift selection                                                       */
  /* ------------------------------------------------------------------ */

  function gwpHandleGiftSelect(btn) {
    var hasVariants = btn.dataset.hasVariants === 'true';
    gwpLog('gwpHandleGiftSelect — variantId:', btn.dataset.variantId, '| hasVariants:', hasVariants);
    if (hasVariants) {
      gwpShowVariantPanel(btn);
    } else {
      // Hide variant panel in case it was open from a previous selection
      var panel = document.getElementById('gwp-variant-panel');
      if (panel) panel.style.display = 'none';
      gwpAddGiftToCart(parseInt(btn.dataset.variantId, 10));
    }
  }

  function gwpShowVariantPanel(btn) {
    document.querySelectorAll('.gwp-select').forEach(function (b) {
      b.classList.remove('is-pending', 'is-selected');
    });
    btn.classList.add('is-pending');

    var panel = document.getElementById('gwp-variant-panel');
    if (!panel) return;

    var idx = parseInt(btn.dataset.giftIdx, 10);
    var gift = gwpCurrentGifts && gwpCurrentGifts[idx];
    if (!gift || !gift.variants) { panel.style.display = 'none'; return; }

    var optionsDiv = panel.querySelector('.gwp-variant-panel__options');
    if (optionsDiv) {
      var btns = '';
      gift.variants.forEach(function (v) {
        var dis = v.available ? '' : ' disabled';
        var cls = v.available ? '' : ' gwp-variant-btn--unavailable';
        btns += '<button class="gwp-variant-btn' + cls + '" data-variant-id="' + v.id + '"' + dis + '>'
          + gwpEscape(v.title) + '</button>';
      });
      optionsDiv.innerHTML = btns;
    }

    panel.style.display = 'block';
    gwpLog('gwpShowVariantPanel — panel shown for gift idx:', idx, '| variants:', gift.variants.length);
  }

  /* ------------------------------------------------------------------ */
  /*  Cart operations                                                      */
  /* ------------------------------------------------------------------ */

  // Cart requests made by gwp.js itself — flagged so the cart-request watcher
  // (below) doesn't treat them as customer changes. The flag is read synchronously
  // when fetch() is called, so resetting it right after is safe.
  function gwpOwnFetch(url, opts) {
    gwpSelfRequest = true;
    try {
      return fetch(url, opts);
    } finally {
      gwpSelfRequest = false;
    }
  }

  function gwpAddGiftToCart(variantId) {
    if (gwpAddInProgress) {
      gwpLog('gwpAddGiftToCart — already in progress, skipping');
      return;
    }
    var data = gwpGetData();
    gwpLog('gwpAddGiftToCart — variantId:', variantId, '| isEligible:', data && gwpCurrencyRecheck(data));
    if (!data || !gwpCurrencyRecheck(data) || !variantId) return;

    gwpAddInProgress = true;
    var swapKey   = gwpSwapLineKey;
    gwpSwapLineKey = null;

    document.querySelectorAll('.gwp-select').forEach(function (b) { b.classList.add('is-loading'); });

    gwpOwnFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _gwp: 'true', _gwp_offer: data.offer.id }
      })
    })
      .then(function (r) {
        gwpLog('gwpAddGiftToCart — /cart/add.js response status:', r.status);
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function (addedItem) {
        gwpLog('gwpAddGiftToCart — gift added successfully:', addedItem.title);
        if (swapKey) {
          gwpLog('gwpAddGiftToCart — removing old gift (swap), lineKey:', swapKey);
          return gwpOwnFetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: swapKey, quantity: 0 })
          }).then(function (r) {
            if (!r.ok) throw new Error('swap remove failed: ' + r.status);
            return r.json();
          });
        }
      })
      .then(function () {
        gwpAddInProgress = false;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'gwp_selected',
          gwp_offer_id: data.offer.id,
          gwp_analytics_id: data.offer.analyticsId,
          gwp_variant_id: variantId
        });

        var maxQty   = (data.offer && data.offer.maxQuantity) || 1;
        var newCount = (data.gwpInCartCount || 0) + (swapKey ? 0 : 1);
        gwpLog('gwpAddGiftToCart — newCount:', newCount, '| maxQty:', maxQty, '| wasSwap:', !!swapKey);

        if (swapKey || newCount >= maxQty) {
          // Swap (replaced existing) or reached max — close popup
          document.querySelectorAll('.gwp-select').forEach(function (b) { b.classList.remove('is-loading', 'is-pending', 'is-selected'); });
          gwpClosePopup();
          gwpLog('gwpAddGiftToCart — closing popup, refreshing cart display');
          gwpRefreshCartDisplay();
        } else {
          // Below max quantity — keep popup open, mark this gift as selected/disabled
          document.querySelectorAll('.gwp-select').forEach(function (b) {
            b.classList.remove('is-loading', 'is-pending');
            if (parseInt(b.dataset.variantId, 10) === variantId) {
              b.classList.add('is-selected', 'is-loading');
            }
          });
          gwpLog('gwpAddGiftToCart — below maxQty, keeping popup open for more selections');
          gwpRefreshCartDisplay();
        }
      })
      .catch(function (err) {
        gwpAddInProgress = false;
        gwpLog('ERROR — gwpAddGiftToCart failed:', err.message);
        document.querySelectorAll('.gwp-select').forEach(function (b) { b.classList.remove('is-loading'); });
      });
  }

  function gwpRemoveGift(data) {
    gwpLog('gwpRemoveGift — lineKey:', data.gwpLineKey);
    if (gwpRemovalInProgress) {
      gwpLog('gwpRemoveGift — already in progress, skipping');
      return;
    }
    gwpRemovalInProgress = true;

    // Capture GWP product title from mini cart DOM before removal so we can
    // identify its SFL popup by title and dismiss only that one.
    var gwpSflTitle = '';
    var gwpSidebarItem = document.querySelector('.cart-sidebar-item--gwp');
    if (gwpSidebarItem) {
      var nameEl = gwpSidebarItem.querySelector('.product-name strong');
      if (nameEl) {
        for (var ni = 0; ni < nameEl.childNodes.length; ni++) {
          if (nameEl.childNodes[ni].nodeType === 3) {
            var txt = nameEl.childNodes[ni].textContent.trim();
            if (txt) { gwpSflTitle = txt.toUpperCase(); break; }
          }
        }
      }
      gwpLog('gwpRemoveGift — GWP title for SFL check:', gwpSflTitle || '(not found in sidebar)');
    }

    gwpOwnFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: data.gwpLineKey, quantity: 0 })
    })
      .then(function (r) {
        gwpLog('gwpRemoveGift — /cart/change.js response status:', r.status);
        if (!r.ok) throw new Error('remove failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        gwpRemovalInProgress = false;
        // Activate suppression only AFTER GWP is removed so Bailey Blouse popup is unaffected
        window._gwpAutoRemoving = true;
        window._gwpSflTitle = gwpSflTitle;
        setTimeout(function () { window._gwpAutoRemoving = false; }, 4000);

        var label = gwpStatusGiftName || 'Free gift';
        gwpLog('gwpRemoveGift — gift removed, showing done status');
        gwpShowStatus(label + ' removed.', 'done');
        gwpAutoHideStatus(3000);

        // Poll for Swym SFL popup: dismiss only if it matches the GWP product title
        var swymDismissCount = 0;
        var swymDismissTimer = setInterval(function () {
          var popup = document.querySelector('.swym-sfl-popup');
          if (popup) {
            var titleEl = popup.querySelector('.swym-sfl-modal-product-title');
            var popupTitle = titleEl ? titleEl.textContent.trim().toUpperCase() : '';
            var isGwpPopup = !gwpSflTitle || popupTitle === gwpSflTitle || popupTitle === '';
            gwpLog('[GWP-SFL] poll: popup="' + popupTitle + '" gwp="' + gwpSflTitle + '" dismiss=' + isGwpPopup);
            if (isGwpPopup) {
              var cancelBtn = popup.querySelector('.swym-sfl-popup-cancel');
              if (cancelBtn) cancelBtn.click();
              clearInterval(swymDismissTimer);
            }
          }
          if (++swymDismissCount >= 15) clearInterval(swymDismissTimer);
        }, 200);

        gwpRefreshCartDisplay();
      })
      .catch(function (err) {
        gwpLog('ERROR — gwpRemoveGift failed:', err.message);
        gwpRemovalInProgress = false;
        window._gwpAutoRemoving = false;
      });
  }

  function gwpCheckAutoRemove() {
    var data = gwpGetData();
    gwpLog('gwpCheckAutoRemove — gwpInCart:', data && data.gwpInCart, '| isEligible:', data && gwpCurrencyRecheck(data));
    if (!data || !data.gwpInCart) {
      if (gwpStatusState === 'checking') gwpHideStatus(300);
      return;
    }
    if (gwpCurrencyRecheck(data)) {
      if (gwpStatusState === 'checking') gwpHideStatus(300);
      return;
    }
    if (gwpRemovalInProgress) return;
    gwpLog('gwpCheckAutoRemove — no longer eligible, removing gift');
    gwpShowStatus('Removing ' + (gwpStatusGiftName || 'free gift') + '…', 'removing');
    gwpRemoveGift(data);
  }

  /* ------------------------------------------------------------------ */
  /*  Status bar                                                          */
  /* ------------------------------------------------------------------ */

  function gwpShowStatus(msg, state) {
    gwpStatusMsg   = msg;
    gwpStatusState = state;
    var bar = document.getElementById('gwp-status-bar');
    if (!bar) return;
    bar.querySelector('.gwp-status-text').textContent = msg;
    bar.className = 'gwp-status-' + state + ' is-visible';
  }

  function gwpHideStatus(delay) {
    if (delay > 0) { setTimeout(function () { gwpHideStatus(0); }, delay); return; }
    gwpStatusMsg   = '';
    gwpStatusState = '';
    var bar = document.getElementById('gwp-status-bar');
    if (bar) bar.classList.remove('is-visible');
  }

  function gwpAutoHideStatus(delay) {
    clearTimeout(gwpStatusHideTimer);
    gwpStatusHideTimer = setTimeout(function () { gwpHideStatus(0); }, delay);
  }

  /* ------------------------------------------------------------------ */
  /*  Event listeners                                                      */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {

    // Close popup
    if (e.target.id === 'gwp-popup__overlay' || e.target.closest('#gwp-popup__close')) {
      gwpClosePopup();
      return;
    }

    // Variant button
    var vBtn = e.target.closest('.gwp-variant-btn');
    if (vBtn && !vBtn.disabled) {
      gwpAddGiftToCart(parseInt(vBtn.dataset.variantId, 10));
      return;
    }

    // Gift card button
    var gBtn = e.target.closest('.gwp-select');
    if (gBtn && !gBtn.classList.contains('is-loading')) {
      gwpHandleGiftSelect(gBtn);
      return;
    }

    // "Change gift" in the selected-state bar
    var changeBtn = e.target.closest('.gwp-selected-state__change');
    if (changeBtn) {
      var data = gwpGetData();
      gwpLog('Change gift clicked — gwpLineKey:', data && data.gwpLineKey);
      if (data && data.canChangeGift && data.gwpLineKey) {
        gwpSwapLineKey = data.gwpLineKey;
        gwpOpenPopup(data);
      }
      return;
    }

    // "Change gift" button inside cart sidebar item
    var changeGiftBtn = e.target.closest('.gwp-change-gift');
    if (changeGiftBtn) {
      var data = gwpGetData();
      var lineKey = changeGiftBtn.dataset.linekey || (data && data.gwpLineKey);
      gwpLog('Change gift (cart item) clicked — lineKey:', lineKey);
      if (data && data.canChangeGift && lineKey) {
        gwpSwapLineKey = lineKey;
        gwpOpenPopup(data);
      }
      return;
    }

    // Cart icon click — show popup if eligible and no gift yet
    var cartTrigger = e.target.closest('.site-header__cart');
    if (cartTrigger) {
      gwpLog('Cart icon clicked — checking eligibility after 350ms');
      setTimeout(function () {
        var data = gwpGetData();
        if (data && gwpCurrencyRecheck(data) && !data.gwpInCart) {
          gwpLog('Cart icon: eligible, opening popup');
          gwpOpenPopup(data);
        }
      }, 350);
    }
  });

  // Block checkout clicks/submits while locked (CSS also disables the buttons;
  // this catches anything CSS can't, e.g. keyboard activation)
  function gwpIsCheckoutTarget(el) {
    return el && el.closest && el.closest('[name="checkout"], a[href^="/checkout"], .additional-checkout-buttons');
  }

  document.addEventListener('click', function (e) {
    if (!gwpIsCheckoutLocked() || !gwpIsCheckoutTarget(e.target)) return;
    gwpLog('Checkout blocked — ineligible gift still in cart');
    e.preventDefault();
    e.stopImmediatePropagation();
    gwpCheckAutoRemove();
  }, true);

  document.addEventListener('submit', function (e) {
    if (!gwpIsCheckoutLocked()) return;
    var submitter = e.submitter;
    var isCheckout = (submitter && submitter.name === 'checkout') || /\/checkout/.test(e.target.getAttribute('action') || '');
    if (!isCheckout) return;
    gwpLog('Checkout submit blocked — ineligible gift still in cart');
    e.preventDefault();
    e.stopImmediatePropagation();
    gwpCheckAutoRemove();
  }, true);

  // Cart item added — use a separate timer so cart:updated cannot cancel it
  document.addEventListener('cart:added', function () {
    gwpLog('Event: cart:added');
    clearTimeout(gwpCartAddedTimer);
    gwpCartAddedTimer = setTimeout(function () {
      gwpLog('cart:added debounce fired — refreshing GWP data');
      gwpRefreshData(function () {
        gwpInit('cart:added');
        gwpCheckAutoRemove();
      });
    }, 400);
  });

  // Cart updated (item removed / qty changed) — check if gift should be removed
  // Re-check eligibility after any cart change (debounced, so bursts of
  // requests/events collapse into one check)
  function gwpScheduleCheck(source) {
    clearTimeout(gwpDebounceTimer);
    gwpDebounceTimer = setTimeout(function () {
      gwpLog(source + ' debounce fired — refreshing GWP data');

      var dataBefore = gwpGetData();
      if (dataBefore && dataBefore.gwpInCart) {
        gwpStatusGiftName = 'Free gift';
        gwpShowStatus('Checking eligibility…', 'checking');
      }

      gwpRefreshData(function (ok) {
        if (!ok) {
          // Couldn't get fresh eligibility — keep checkout locked rather than
          // trusting stale data (a page load re-evaluates server-side)
          gwpLog(source + ' — refresh failed, leaving checkout lock as is');
          return;
        }
        gwpInit(source);
        gwpCheckAutoRemove();
      });
    }, 250);
  }

  document.addEventListener('cart:updated', function () {
    gwpLog('Event: cart:updated');
    gwpScheduleCheck('cart:updated');
  });

  /* ------------------------------------------------------------------ */
  /*  Cart-request watcher                                                */
  /* ------------------------------------------------------------------ */
  // Not every cart control fires cart:updated (e.g. the cart page Remove button
  // posts /cart/change.js directly), so watch the cart requests themselves.
  // When an item-changing request starts and a gift is in the cart, checkout is
  // locked until the re-check confirms the cart still qualifies.

  function gwpIsCartMutation(url, body) {
    var m = String(url || '').match(/\/cart\/(change|update|add|clear)(?:\.js|\.json)?(?:[?#]|$)/);
    if (!m) return false;
    // Attribute/note-only updates (e.g. bundle discount attributes) don't change items
    if (m[1] === 'update' && typeof body === 'string') return body.indexOf('updates') !== -1;
    return true;
  }

  function gwpOnCartMutationStart() {
    var el = document.querySelector('#gwp-data');
    if (el && /"gwpInCart":\s*true/.test(el.textContent)) gwpSetCheckoutLock(true);
  }

  function gwpOnCartMutationDone() {
    gwpScheduleCheck('cart-request');
  }

  var gwpOrigFetch = window.fetch;
  if (gwpOrigFetch) {
    window.fetch = function (input, init) {
      var url = (input && input.url) ? input.url : String(input);
      if (gwpSelfRequest || !gwpIsCartMutation(url, init && init.body)) {
        return gwpOrigFetch.apply(this, arguments);
      }
      gwpOnCartMutationStart();
      var request = gwpOrigFetch.apply(this, arguments);
      request.then(gwpOnCartMutationDone, gwpOnCartMutationDone);
      return request;
    };
  }

  var gwpOrigXhrOpen = XMLHttpRequest.prototype.open;
  var gwpOrigXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._gwpUrl = url;
    return gwpOrigXhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (!gwpSelfRequest && gwpIsCartMutation(this._gwpUrl, body)) {
      gwpOnCartMutationStart();
      this.addEventListener('loadend', gwpOnCartMutationDone);
    }
    return gwpOrigXhrSend.apply(this, arguments);
  };

  // Page ready — apply initial state (shows popup if already eligible on load)
  document.addEventListener('DOMContentLoaded', function () {
    gwpLog('DOMContentLoaded — running gwpInit');
    gwpInit('DOMContentLoaded');
    gwpCheckAutoRemove();

    // Dismiss Swym SFL notification when GWP item is auto-removed by the system.
    // The CSS rule body.gwp-auto-removing already hides it; this observer is a
    // secondary layer that also removes the swym-showing class so Swym's internal
    // state stays in sync regardless of whether the element pre-exists or is created fresh.
    // Swym shows its SFL popup by adding 'swym-sfl-popup' class to a persistent
    // .swym-modal element (z-index jumps from -999999999 to 2147483647).
    // Only dismiss if the popup is for the GWP product (matched by title).
    function gwpDismissSwymSflPopup(el) {
      if (!el || !el.classList) return false;
      if (el.classList.contains('swym-sfl-popup')) {
        var titleEl = el.querySelector('.swym-sfl-modal-product-title');
        var popupTitle = titleEl ? titleEl.textContent.trim().toUpperCase() : '';
        var gwpTitle = window._gwpSflTitle || '';
        var isGwpPopup = !gwpTitle || popupTitle === gwpTitle || popupTitle === '';
        gwpLog('[GWP-SFL] observer: popup="' + popupTitle + '" gwp="' + gwpTitle + '" dismiss=' + isGwpPopup);
        if (isGwpPopup) {
          var cancelBtn = el.querySelector('.swym-sfl-popup-cancel');
          if (cancelBtn) cancelBtn.click();
          return true;
        }
      }
      return false;
    }
    var swymNotifObserver = new MutationObserver(function (mutations) {
      if (!document.body.classList.contains('gwp-auto-removing')) return;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes' && m.attributeName === 'class') {
          if (gwpDismissSwymSflPopup(m.target)) break;
        }
        if (m.type === 'childList') {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var node = m.addedNodes[j];
            if (node.nodeType !== 1) continue;
            if (gwpDismissSwymSflPopup(node)) break;
            var inner = node.querySelector && node.querySelector('.swym-sfl-popup');
            if (inner && gwpDismissSwymSflPopup(inner)) break;
          }
        }
      }
    });
    swymNotifObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class']
    });
  });

})();
